// Words preserved as-authored when running through `toSentenceCase`. Acronyms,
// mixed-case tech / OS names, and brand / product names live here. Match is
// case-sensitive against the punctuation-stripped form of each token, so
// "OAuth," matches "OAuth".
//
// This list grows over time — add new entries here rather than threading
// per-string overrides through nav configs.
export const SENTENCE_CASE_EXCEPTIONS: ReadonlySet<string> = new Set([
  // Acronyms (preserve all-caps)
  'API',
  'APIs',
  'URL',
  'URLs',
  'HTTP',
  'HTTPS',
  'JSON',
  'XML',
  'HTML',
  'CSS',
  'JWT',
  'SSO',
  'SAML',
  'OAuth',
  'IPP',
  'SFP',
  'PBI',
  'CPG',
  'KPI',
  'KPIs',
  'ROI',
  'POS',
  'ETA',
  'FAQ',
  'FAQs',
  'PDF',
  'CSV',
  'ID',
  'IDs',
  'SKU',
  'SKUs',
  'UPC',
  'UPCs',
  'GTIN',
  'EAN',
  'B2B',
  'B2C',
  'A/B',
  'RTO',
  'AOV',
  'GMV',
  'LMD',
  'OOD',
  // Mixed-case tech / OS
  'iOS',
  'iPadOS',
  'macOS',
  'GraphQL',
  'JavaScript',
  'TypeScript',
  'GitHub',
  'GitLab',
  'MySQL',
  'PostgreSQL',
  // Brands / product names
  'Instacart',
  'Carrot',
  'Caper',
  'Storefront',
  'Marketplace',
  'Connect',
  'Pro',
  'Ads',
])

const PUNCTUATION_RE = /[^\p{L}\p{N}]/gu

const stripPunctuation = (word: string): string => word.replace(PUNCTUATION_RE, '')

// True when the word has any uppercase letter past position 0 (after stripping
// punctuation). Catches mixed-case tokens like "GraphQL" or "iOS" even when
// they aren't in the exception list, so newly-coined product names survive.
const hasInternalCapitals = (word: string): boolean => {
  const stripped = stripPunctuation(word)
  if (stripped.length <= 1) return false
  const tail = stripped.slice(1)
  return tail !== tail.toLowerCase()
}

const isException = (word: string): boolean => SENTENCE_CASE_EXCEPTIONS.has(stripPunctuation(word))

// Capitalize the first letter and lowercase all subsequent letters of every
// word past the first. Tokens matching an exception or containing internal
// capitals are preserved as authored. Whitespace separators are preserved
// exactly so the join round-trips the original spacing.
export const toSentenceCase = (input: string): string => {
  if (!input) return input

  const tokens = input.split(/(\s+)/)
  let firstWordSeen = false

  return tokens
    .map(token => {
      if (token === '' || /^\s+$/.test(token)) return token

      const preserve = isException(token) || hasInternalCapitals(token)

      if (!firstWordSeen) {
        firstWordSeen = true
        if (preserve) return token
        const lower = token.toLowerCase()
        return lower.charAt(0).toUpperCase() + lower.slice(1)
      }

      return preserve ? token : token.toLowerCase()
    })
    .join('')
}
